import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaHeart, FaPaperPlane, FaInbox, FaHandshake, FaCheck, FaTimes,
  FaComments, FaUserCircle, FaCheckCircle, FaMapMarkerAlt,
  FaBriefcase, FaGraduationCap, FaSpinner
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// Skeleton
const MatchSkeleton = () => (
  <div className="bg-white rounded-xl shadow-sm p-4 animate-pulse flex items-center gap-4">
    <div className="w-16 h-16 bg-gray-200 rounded-full flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-5 bg-gray-200 rounded w-1/2" />
      <div className="h-4 bg-gray-200 rounded w-1/3" />
      <div className="h-4 bg-gray-200 rounded w-2/3" />
    </div>
    <div className="w-20 h-8 bg-gray-200 rounded" />
  </div>
);

// Match Card
const MatchCard = ({ interest, type, onAccept, onReject, processing }) => {
  const profile = interest.profile || interest.sender || interest.receiver || interest;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      <div className="flex flex-col sm:flex-row">
        {/* Photo */}
        <div className="sm:w-32 h-32 sm:h-auto flex-shrink-0">
          {profile.photos?.[0] ? (
            <img src={profile.photos[0]} alt={profile.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#8B0000] to-[#DC143C] flex items-center justify-center min-h-[128px]">
              <FaUserCircle className="text-white/40 text-5xl" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-[#8B0000] font-['Playfair_Display']">
                  {profile.name || 'Profile'}
                </h3>
                {profile.isVerified && (
                  <FaCheckCircle className="text-green-500 text-sm" />
                )}
              </div>
              <p className="text-sm text-gray-500 font-mono">
                ID: #{profile.profileId || profile._id?.slice(-6)}
              </p>
            </div>

            {/* Status Badge for Sent tab */}
            {type === 'sent' && interest.status && (
              <span className={`text-xs px-3 py-1 rounded-full font-medium font-['Noto_Sans_Devanagari'] ${
                interest.status === 'accepted' ? 'bg-green-100 text-green-700' :
                interest.status === 'rejected' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700'
              }`}>
                {interest.status === 'accepted' ? 'स्वीकारले' : interest.status === 'rejected' ? 'नाकारले' : 'प्रतीक्षेत'}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
            {profile.age && (
              <span className="font-['Noto_Sans_Devanagari']">{profile.age} वर्षे</span>
            )}
            {profile.height && <span>{profile.height}</span>}
            {profile.city && (
              <span className="flex items-center gap-1">
                <FaMapMarkerAlt className="text-[#DC143C] text-xs" /> {profile.city}
              </span>
            )}
            {profile.qualification && (
              <span className="flex items-center gap-1">
                <FaGraduationCap className="text-[#B8860B] text-xs" /> {profile.qualification}
              </span>
            )}
            {profile.occupation && (
              <span className="flex items-center gap-1">
                <FaBriefcase className="text-[#B8860B] text-xs" /> {profile.occupation}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={`/profile/${profile._id}`}
              className="px-4 py-2 text-sm border border-[#8B0000] text-[#8B0000] rounded-lg hover:bg-[#8B0000] hover:text-white transition-colors"
            >
              प्रोफाइल पहा
            </Link>

            {type === 'received' && interest.status !== 'accepted' && interest.status !== 'rejected' && (
              <>
                <button
                  onClick={() => onAccept(interest._id)}
                  disabled={processing}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                  <span className="font-['Noto_Sans_Devanagari']">स्वीकारा</span>
                </button>
                <button
                  onClick={() => onReject(interest._id)}
                  disabled={processing}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {processing ? <FaSpinner className="animate-spin" /> : <FaTimes />}
                  <span className="font-['Noto_Sans_Devanagari']">नाकारा</span>
                </button>
              </>
            )}

            {type === 'mutual' && (
              <Link
                to={`/chat/${profile._id}`}
                className="flex items-center gap-1.5 px-4 py-2 text-sm bg-[#8B0000] text-white rounded-lg hover:bg-[#DC143C] transition-colors font-['Noto_Sans_Devanagari']"
              >
                <FaComments /> चॅट करा
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MatchesPage = () => {
  const { api } = useAuth();
  const [activeTab, setActiveTab] = useState('sent');
  const [data, setData] = useState({ sent: [], received: [], mutual: [] });
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchAllInterests();
  }, []);

  const fetchAllInterests = async () => {
    setLoading(true);
    try {
      const [sentRes, receivedRes, mutualRes] = await Promise.allSettled([
        api.get('/api/interests/sent'),
        api.get('/api/interests/received'),
        api.get('/api/interests/mutual')
      ]);

      setData({
        sent: sentRes.status === 'fulfilled' ? (sentRes.value.data.interests || sentRes.value.data || []) : [],
        received: receivedRes.status === 'fulfilled' ? (receivedRes.value.data.interests || receivedRes.value.data || []) : [],
        mutual: mutualRes.status === 'fulfilled' ? (mutualRes.value.data.interests || mutualRes.value.data || []) : []
      });
    } catch (error) {
      console.error('Failed to fetch interests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (interestId) => {
    setProcessing(true);
    try {
      await api.post(`/api/interests/${interestId}/accept`);
      toast.success('रस स्वीकारला!');
      fetchAllInterests();
    } catch (error) {
      toast.error('स्वीकारण्यात अयशस्वी');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (interestId) => {
    setProcessing(true);
    try {
      await api.post(`/api/interests/${interestId}/reject`);
      toast.success('रस नाकारला');
      fetchAllInterests();
    } catch (error) {
      toast.error('नाकारण्यात अयशस्वी');
    } finally {
      setProcessing(false);
    }
  };

  const tabs = [
    { key: 'sent', label: 'पाठवलेले', icon: FaPaperPlane },
    { key: 'received', label: 'आलेले', icon: FaInbox },
    { key: 'mutual', label: 'जुळलेले', icon: FaHandshake }
  ];

  const emptyMessages = {
    sent: {
      title: 'अजून कोणाला रस पाठवला नाही',
      subtitle: 'प्रोफाइल ब्राउज करा आणि आवडलेल्या व्यक्तीला रस पाठवा',
      cta: { label: 'प्रोफाइल शोधा', to: '/browse' }
    },
    received: {
      title: 'अजून कोणाचा रस आलेला नाही',
      subtitle: 'तुमचे प्रोफाइल पूर्ण करा जेणेकरून अधिक लोकांना आवडेल',
      cta: { label: 'प्रोफाइल पूर्ण करा', to: '/my-profile' }
    },
    mutual: {
      title: 'अजून कोणाशी जुळले नाही',
      subtitle: 'जेव्हा दोन्ही बाजूंनी रस व्यक्त होतो तेव्हा इथे दिसेल',
      cta: null
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0] flex flex-col">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-r from-[#8B0000] to-[#DC143C] py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl md:text-3xl font-bold text-white font-['Noto_Sans_Devanagari'] flex items-center gap-3">
            <FaHeart className="text-[#FFD700]" /> जुळवणी
          </h1>
          <p className="text-[#FFD700]/80 text-sm font-['Playfair_Display'] mt-1">Matches &amp; Interests</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex-1">
        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm p-1 flex gap-1 mb-6 border border-gray-100">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium rounded-lg transition-all font-['Noto_Sans_Devanagari'] ${
                activeTab === tab.key
                  ? 'bg-[#8B0000] text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <tab.icon className="text-xs" />
              <span>{tab.label}</span>
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab.key ? 'bg-white/20' : 'bg-gray-200'
              }`}>
                {data[tab.key].length}
              </span>
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => <MatchSkeleton key={i} />)}
          </div>
        ) : data[activeTab].length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-sm">
            <div className="w-20 h-20 mx-auto mb-6 bg-[#8B0000]/10 rounded-full flex items-center justify-center">
              <FaHeart className="text-[#8B0000] text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-[#8B0000] font-['Noto_Sans_Devanagari'] mb-2">
              {emptyMessages[activeTab].title}
            </h3>
            <p className="text-gray-500 font-['Noto_Sans_Devanagari'] mb-6">
              {emptyMessages[activeTab].subtitle}
            </p>
            {emptyMessages[activeTab].cta && (
              <Link
                to={emptyMessages[activeTab].cta.to}
                className="inline-flex items-center gap-2 bg-[#8B0000] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#DC143C] transition-colors font-['Noto_Sans_Devanagari']"
              >
                {emptyMessages[activeTab].cta.label}
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {data[activeTab].map((interest, idx) => (
              <MatchCard
                key={interest._id || idx}
                interest={interest}
                type={activeTab}
                onAccept={handleAccept}
                onReject={handleReject}
                processing={processing}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default MatchesPage;
